import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Chip,
  TextField,
  Tooltip,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Collapse,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Description as FileIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Edit as EditIcon,
  BugReport as BugIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';
import atlasDebugService, { AtlasDebugFile, AtlasDebugAnalysis, AtlasTestResponse } from '../services/atlasDebugService';
import ModuleAuthModal from '../shared/components/ModuleAuthModal';
import authService from '../shared/services/authService';

const AtlasDebugPanel: React.FC = () => {
  const [files, setFiles] = useState<AtlasDebugFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<AtlasDebugFile | null>(null);
  const [fileContent, setFileContent] = useState<any>(null);
  const [editedContent, setEditedContent] = useState<string>('');
  const [analysis, setAnalysis] = useState<AtlasDebugAnalysis | null>(null);
  const [testResponse, setTestResponse] = useState<AtlasTestResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(true);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [atlasModuleId, setAtlasModuleId] = useState<string>('ebb342e7-3ffd-487d-8910-f03d91678eb9'); // BEO ATLAS module ID

  useEffect(() => {
    loadFiles();
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check actual authentication status from backend
      const status = await atlasDebugService.checkAuthStatus();
      setIsAuthenticated(status.authenticated);
      if (status.module_id) {
        setAtlasModuleId(status.module_id);
      }
    } catch (err) {
      // If we can't check, assume not authenticated
      setIsAuthenticated(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      const result = await atlasDebugService.logout();
      setSuccess(result.message);
      setIsAuthenticated(false);
      // Clear any test results
      setTestResponse(null);
    } catch (err: any) {
      setError(err.message || 'Failed to logout');
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      setLoading(true);
      const fileList = await atlasDebugService.getDebugFiles();
      setFiles(fileList);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load debug files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (file: AtlasDebugFile) => {
    try {
      setLoading(true);
      setSelectedFile(file);
      const content = await atlasDebugService.getFileContent(file.filename);
      setFileContent(content);
      setEditedContent(JSON.stringify(content, null, 2));
      setEditMode(false);
      setAnalysis(null);
      setTestResponse(null);
      setJsonError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load file content');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    try {
      setLoading(true);
      const contentToAnalyze = editMode ? JSON.parse(editedContent) : fileContent;
      const result = await atlasDebugService.analyzeStructure(contentToAnalyze);
      setAnalysis(result);
      setShowAnalysis(true);
      setSuccess('Structure analysis completed');
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setJsonError('Invalid JSON format');
      } else {
        setError(err.message || 'Failed to analyze structure');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    try {
      setLoading(true);
      setTestResponse(null);
      const contentToSend = editMode ? JSON.parse(editedContent) : fileContent;
      const result = await atlasDebugService.testSubmission(contentToSend);
      setTestResponse(result);

      // Check for authentication errors
      // Don't auto-prompt for "token nicht aktuell" - that's an application token issue, not OAuth
      if (result.auth_error && result.auth_message && !result.auth_message.includes('nicht aktuell')) {
        setError(result.error || 'Authentication expired. Please re-authenticate with BEO ATLAS.');
        setIsAuthenticated(false);
        // Refresh auth status
        await checkAuthStatus();
        // Open auth modal automatically
        setTimeout(() => setAuthModalOpen(true), 1000);
      } else if (result.auth_error && result.auth_message && result.auth_message.includes('nicht aktuell')) {
        // Application token issue - don't prompt for OAuth re-auth
        setError(`BEO ATLAS Error: ${result.auth_message}\n\nThis is an application token issue. The OAuth authentication is working. Please check the application token configuration.`);
      } else if (result.success) {
        setSuccess('Test submission successful!');
      } else {
        setError(`Test failed: ${result.error || 'Unknown error'}`);
      }

      // Refresh file list if new files were saved
      if (result.request_saved_as || result.response_saved_as) {
        await loadFiles();
      }
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setJsonError('Invalid JSON format');
      } else if (err.response?.status === 401 && err.response?.data?.detail?.includes('No valid OAuth token')) {
        // Authentication required
        setError('Authentication required. Please authenticate with BEO ATLAS first.');
        setIsAuthenticated(false);
        setAuthModalOpen(true);
      } else {
        setError(err.message || 'Failed to test submission');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearFiles = async () => {
    if (!window.confirm('Are you sure you want to delete all debug files?')) {
      return;
    }

    try {
      setLoading(true);
      const result = await atlasDebugService.clearDebugFiles();
      setSuccess(`${result.deleted_count} files deleted`);
      setFiles([]);
      setSelectedFile(null);
      setFileContent(null);
      setEditedContent('');
      setAnalysis(null);
      setTestResponse(null);
    } catch (err: any) {
      setError(err.message || 'Failed to clear files');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (value: string) => {
    setEditedContent(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch {
      setJsonError('Invalid JSON format');
    }
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(editedContent);
    setSuccess('Content copied to clipboard');
  };

  const handleDownloadContent = () => {
    const blob = new Blob([editedContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedFile?.filename || 'atlas-debug.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'cleaned':
        return <SuccessIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'response':
        return <WarningIcon color="warning" />;
      default:
        return <FileIcon color="primary" />;
    }
  };

  const getFileTypeChip = (type: string) => {
    const colors: any = {
      original: 'default',
      cleaned: 'success',
      response: 'warning',
      error: 'error'
    };
    return <Chip label={type.toUpperCase()} size="small" color={colors[type] || 'default'} />;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* File List Section */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '80vh', overflow: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Debug Files</Typography>
              <Box>
                <Tooltip title="Refresh">
                  <IconButton onClick={loadFiles} size="small">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear All Files">
                  <IconButton onClick={handleClearFiles} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {loading && !files.length ? (
              <CircularProgress />
            ) : files.length === 0 ? (
              <Alert severity="info">No debug files available</Alert>
            ) : (
              <List>
                {files.map((file) => (
                  <ListItem
                    key={file.filename}
                    button
                    selected={selectedFile?.filename === file.filename}
                    onClick={() => handleFileSelect(file)}
                    sx={{ mb: 1, borderRadius: 1 }}
                  >
                    <ListItemIcon>
                      {getFileIcon(file.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" noWrap>
                            {atlasDebugService.formatTimestamp(file.filename)}
                          </Typography>
                          {getFileTypeChip(file.type)}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          {atlasDebugService.formatFileSize(file.size)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Content Editor Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '80vh', overflow: 'auto' }}>
            {selectedFile ? (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h6">
                      {selectedFile.filename}
                    </Typography>
                    {/* Authentication Status Indicator */}
                    {isAuthenticated !== null && (
                      <Chip
                        icon={isAuthenticated ? <LockOpenIcon /> : <LockIcon />}
                        label={isAuthenticated ? "Authenticated" : "Not Authenticated"}
                        color={isAuthenticated ? "success" : "warning"}
                        size="small"
                        onClick={isAuthenticated ? undefined : () => setAuthModalOpen(true)}
                        sx={{ cursor: isAuthenticated ? 'default' : 'pointer' }}
                      />
                    )}
                  </Box>
                  <Box>
                    {!isAuthenticated ? (
                      <Button
                        variant="outlined"
                        startIcon={<KeyIcon />}
                        onClick={() => setAuthModalOpen(true)}
                        sx={{ mr: 1 }}
                        color="warning"
                      >
                        Authenticate
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<LockIcon />}
                        onClick={handleLogout}
                        sx={{ mr: 1 }}
                        color="error"
                        disabled={loading}
                      >
                        Logout
                      </Button>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editMode}
                          onChange={(e) => setEditMode(e.target.checked)}
                        />
                      }
                      label="Edit Mode"
                    />
                    <Tooltip title="Copy">
                      <IconButton onClick={handleCopyContent}>
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton onClick={handleDownloadContent}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      startIcon={<BugIcon />}
                      onClick={handleAnalyze}
                      sx={{ ml: 1 }}
                      disabled={loading || !!jsonError}
                    >
                      Analyze
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleTest}
                      sx={{ ml: 1 }}
                      disabled={loading || !!jsonError}
                    >
                      Test Submit
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* JSON Editor */}
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  value={editedContent}
                  onChange={(e) => handleContentChange(e.target.value)}
                  disabled={!editMode}
                  error={!!jsonError}
                  helperText={jsonError}
                  sx={{
                    fontFamily: 'monospace',
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem'
                    }
                  }}
                />

                {/* Analysis Results */}
                {analysis && (
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" gutterBottom>
                          Structure Analysis
                        </Typography>
                        <IconButton onClick={() => setShowAnalysis(!showAnalysis)}>
                          {showAnalysis ? <CollapseIcon /> : <ExpandIcon />}
                        </IconButton>
                      </Box>

                      <Collapse in={showAnalysis}>
                        <Stack spacing={2} mt={2}>
                          {/* Overall Status */}
                          <Alert severity={analysis.valid ? 'success' : 'error'}>
                            {analysis.valid ? 'Structure is valid' : 'Structure has issues'}
                          </Alert>

                          {/* Missing Required Fields */}
                          {analysis.missing_required_fields.length > 0 && (
                            <Alert severity="error">
                              <AlertTitle>Missing Required Fields</AlertTitle>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {analysis.missing_required_fields.map((field, i) => (
                                  <li key={i}>{field}</li>
                                ))}
                              </ul>
                            </Alert>
                          )}

                          {/* Invalid Fields */}
                          {analysis.invalid_fields.length > 0 && (
                            <Alert severity="warning">
                              <AlertTitle>Invalid Fields (will be removed)</AlertTitle>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {analysis.invalid_fields.map((field, i) => (
                                  <li key={i}>{field}</li>
                                ))}
                              </ul>
                            </Alert>
                          )}

                          {/* Misplaced Fields */}
                          {analysis.misplaced_fields.length > 0 && (
                            <Alert severity="warning">
                              <AlertTitle>Misplaced Fields</AlertTitle>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {analysis.misplaced_fields.map((item, i) => (
                                  <li key={i}>
                                    <strong>{item.field}</strong>: {item.current_location} → {item.correct_location}
                                  </li>
                                ))}
                              </ul>
                            </Alert>
                          )}

                          {/* Date Issues */}
                          {analysis.date_issues.length > 0 && (
                            <Alert severity="warning">
                              <AlertTitle>Date Issues</AlertTitle>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {analysis.date_issues.map((item, i) => (
                                  <li key={i}>
                                    <strong>{item.field}</strong>: {item.issue} (value: {item.value})
                                  </li>
                                ))}
                              </ul>
                            </Alert>
                          )}

                          {/* Recommendations */}
                          {analysis.recommendations.length > 0 && (
                            <Alert severity="info">
                              <AlertTitle>Recommendations</AlertTitle>
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {analysis.recommendations.map((rec, i) => (
                                  <li key={i}>{rec}</li>
                                ))}
                              </ul>
                            </Alert>
                          )}
                        </Stack>
                      </Collapse>
                    </CardContent>
                  </Card>
                )}

                {/* Test Response */}
                {testResponse && (
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Test Result
                      </Typography>

                      <Alert severity={testResponse.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                        <AlertTitle>
                          {testResponse.success ? 'Success' : 'Failed'} - Status: {testResponse.status_code}
                        </AlertTitle>
                        {testResponse.error && <Typography>{testResponse.error}</Typography>}
                      </Alert>

                      {testResponse.error_details && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <AlertTitle>Error Details</AlertTitle>
                          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                            {JSON.stringify(testResponse.error_details, null, 2)}
                          </pre>
                        </Alert>
                      )}

                      {testResponse.response && (
                        <Alert severity="info">
                          <AlertTitle>Response</AlertTitle>
                          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                            {JSON.stringify(testResponse.response, null, 2)}
                          </pre>
                        </Alert>
                      )}

                      {(testResponse.request_saved_as || testResponse.response_saved_as) && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <AlertTitle>Files Saved</AlertTitle>
                          {testResponse.request_saved_as && (
                            <Typography variant="body2">Request: {testResponse.request_saved_as}</Typography>
                          )}
                          {testResponse.response_saved_as && (
                            <Typography variant="body2">Response: {testResponse.response_saved_as}</Typography>
                          )}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography variant="h6" color="textSecondary">
                  Select a file to view its content
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Module Authentication Modal */}
      <ModuleAuthModal
        open={authModalOpen}
        onClose={(authenticated) => {
          setAuthModalOpen(false);
          if (authenticated) {
            setIsAuthenticated(true);
            setSuccess('Successfully authenticated with BEO ATLAS!');
            checkAuthStatus(); // Refresh auth status
          }
        }}
        moduleId={atlasModuleId}
        moduleName="BEO ATLAS"
      />
    </Box>
  );
};

export default AtlasDebugPanel;